// frontend/src/components/ui.js — small shared pieces for the plan dashboards.
import React from 'react';

export const fmt  = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
export const fmt0 = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
export const pct  = (x, d = 1) => x == null || !Number.isFinite(Number(x)) ? '—' : `${(Number(x) * 100).toFixed(d)}%`;
export const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
export const currentQuarter = () => { const n = new Date(); return `${n.getUTCFullYear()}-Q${Math.ceil((n.getUTCMonth() + 1) / 3)}`; };

// Quarters from `from` through the current quarter + 1.
export function quarterOptionsFrom(from = '2026-Q4') {
  const now = new Date();
  const curY = now.getUTCFullYear(), curQ = Math.ceil((now.getUTCMonth() + 1) / 3);
  let [y, q] = from.split('-Q').map(Number);
  const out = [];
  while (out.length < 16) {
    out.push(`${y}-Q${q}`);
    if (y > curY || (y === curY && q > curQ)) break;
    if (q === 4) { y++; q = 1; } else q++;
  }
  return out;
}

export function Tile({ label, value, sub, accent = 'blue', big = false }) {
  const color = accent === 'green' ? 'var(--green)' : accent === 'amber' ? 'var(--amber)' : accent === 'red' ? 'var(--red)' : accent === 'muted' ? 'var(--text-muted)' : 'var(--blue-primary)';
  return (
    <div className="card" style={{ padding: '16px 18px', borderColor: big ? 'var(--border-bright)' : undefined }}>
      <div className="label" style={{ marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: big ? '26px' : '22px', fontWeight: 700, color, fontFamily: 'var(--font-mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

export function CardTitle({ n, title, tag, sub, color = 'var(--blue-primary)', right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
      {n && <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: color, color: '#fff', fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px' }}>{n}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          {tag && <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{tag}</span>}
        </div>
        {sub && <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

export function Step({ n, desc, detail, value, total = false, tone }) {
  const color = tone === 'green' ? 'var(--green)' : tone === 'red' ? 'var(--red)' : tone === 'amber' ? 'var(--amber)' : tone === 'muted' ? 'var(--text-muted)' : 'var(--text-primary)';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: total ? '12px 0 4px' : '8px 0', borderTop: total ? '1px solid var(--border)' : undefined, marginTop: total ? '6px' : 0 }}>
      {n && <span style={{ width: '20px', height: '20px', borderRadius: '4px', background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: '11px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>{n}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: total ? '14px' : '13.5px', fontWeight: total ? 600 : 400, color: total ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{desc}</div>
        {detail && <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>{detail}</div>}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: total ? 700 : 500, fontSize: total ? '16px' : '14px', color, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

export function Notice({ tone = 'muted', tight, children }) {
  const map = { amber: ['var(--amber-subtle)', 'var(--amber)'], red: ['var(--red-subtle)', 'var(--red)'], muted: ['var(--bg-elevated)', 'var(--border-bright)'] };
  const [bg, bd] = map[tone] || map.muted;
  return (
    <div style={{ background: bg, borderLeft: `3px solid ${bd}`, borderRadius: 'var(--radius-sm)', padding: tight ? '10px 14px' : '14px 18px', margin: tight ? '10px 0 0' : '0 0 18px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

export function Kv({ label, value, sub }) {
  return (
    <div>
      <div className="label" style={{ marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}
export function Field({ label, children, wide }) {
  return <div style={wide ? { gridColumn: '1 / -1' } : undefined}><div className="label" style={{ marginBottom: '6px' }}>{label}</div>{children}</div>;
}

// A horizontal track with labelled ticks and a marker at `value` (all in the same unit).
export function Track({ value, ticks, max, floorAt, color = 'linear-gradient(90deg, #0079C2, #00A4E4)' }) {
  const fill = Math.min(100, Math.max(0, value / max * 100));
  return (
    <div style={{ position: 'relative', height: '14px', borderRadius: '7px', background: 'rgba(127,127,127,0.16)', border: '1px solid var(--border)', margin: '14px 0 24px' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fill}%`, borderRadius: '7px', background: color, boxShadow: '0 0 12px rgba(0,164,228,0.4)' }} />
      {ticks.map(t => {
        const x = Math.min(100, t.at / max * 100);
        const isFloor = floorAt != null && Math.abs(t.at - floorAt) < 1e-9;
        return (
          <div key={t.label} style={{ position: 'absolute', left: `${x}%`, top: isFloor ? '-5px' : '-2px', bottom: isFloor ? '-5px' : '-2px', width: isFloor ? '3px' : '1px', background: isFloor ? '#EEBB3F' : 'rgba(127,127,127,0.6)', transform: 'translateX(-50%)' }}>
            <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px', fontSize: '10px', color: isFloor ? '#EEBB3F' : 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', fontWeight: isFloor ? 700 : 400 }}>{t.label}</span>
          </div>
        );
      })}
      <div style={{ position: 'absolute', left: `${fill}%`, top: '50%', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', border: '3px solid #00A4E4', transform: 'translate(-50%, -50%)', boxShadow: '0 0 0 4px rgba(0,164,228,0.25)' }} />
    </div>
  );
}

const ROLE_LABEL = { rep: 'Rep', manager: 'Manager', finance_admin: 'Finance Admin', global_admin: 'Global Admin' };
export function MembersCard({ label, members = [] }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
        <h3>{label} team ({members.length})</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Department membership and comp plans are set on Finance → Users.</p>
      </div>
      {members.length === 0 ? <div style={{ padding: '24px', color: 'var(--text-muted)', fontSize: '14px' }}>No one is assigned to {label} yet.</div> : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
          <tbody>{members.map(m => (
            <tr key={m.id} style={{ opacity: m.active ? 1 : 0.55 }}>
              <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{m.display_name || '—'}</td>
              <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{m.email || '—'}</td>
              <td>{ROLE_LABEL[m.app_role] || m.app_role}</td>
              <td><span className={`badge ${m.active ? 'badge-paid' : 'badge-disputed'}`}>{m.active ? 'Active' : 'Inactive'}</span></td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}
